// src/pages/TermsPage.js
import React from 'react';
import LegalDocument from '../components/legal/LegalDocument';
import AnimatedGavel from '../components/legal/AnimatedGavel';
import useSEO from '../hooks/useSEO';

const SECTIONS = [
  {
    id: 'acceptance',
    number: 1,
    title: 'Acceptance of Terms',
    blocks: [
      { type: 'p', text: 'By accessing or using Dub Labs services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you are using our Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.' },
    ],
  },
  {
    id: 'service-description',
    number: 2,
    title: 'Service Description',
    blocks: [
      { type: 'p', text: 'Dub Labs provides AI-powered video dubbing and translation services, including:' },
      {
        type: 'ul',
        items: [
          'Video upload and processing',
          'Voice cloning and translation via integrated third-party services',
          'Lip-synchronization technology',
          'White-label platform capabilities for enterprise customers',
        ],
      },
    ],
  },
  {
    id: 'account',
    number: 3,
    title: 'Account Registration and Security',
    blocks: [
      { type: 'h3', text: '3.1 Account Creation' },
      {
        type: 'ul',
        items: [
          'Accounts are created by organization administrators',
          'You must provide accurate and complete information',
          'You are responsible for maintaining account security',
          'One person may not maintain multiple accounts',
        ],
      },
      { type: 'h3', text: '3.2 Account Security' },
      {
        type: 'ul',
        items: [
          'You are responsible for all activities under your account',
          'You must immediately notify us of unauthorized access',
          'We reserve the right to suspend accounts that pose security risks',
        ],
      },
    ],
  },
  {
    id: 'acceptable-use',
    number: 4,
    title: 'Acceptable Use Policy',
    blocks: [
      { type: 'h3', text: '4.1 Permitted Use' },
      { type: 'p', text: 'You may use our Services only for lawful purposes and in accordance with these Terms.' },
      { type: 'h3', text: '4.2 Prohibited Uses' },
      { type: 'p', text: 'You may not:' },
      {
        type: 'ul',
        items: [
          'Upload content that infringes intellectual property rights',
          'Process content that is illegal, harmful, or offensive',
          'Use the Services to create deepfakes for deceptive purposes',
          'Attempt to reverse engineer or hack our systems',
          'Resell or redistribute our Services without authorization',
          'Exceed usage limits or circumvent access controls',
          'Use the Services for competitive analysis or benchmarking',
        ],
      },
    ],
  },
  {
    id: 'content',
    number: 5,
    title: 'Content Ownership and Licensing',
    blocks: [
      { type: 'h3', text: '5.1 Your Content' },
      {
        type: 'ul',
        items: [
          'You retain all rights to content you upload',
          'You grant us a limited license to process your content through our Services',
          'You represent that you have all necessary rights to the content',
          'You are solely responsible for your content',
          'You must have all necessary rights and permissions for any content you upload for dubbing',
        ],
      },
      { type: 'h3', text: '5.2 Our Technology' },
      {
        type: 'ul',
        items: [
          'We retain all rights to our platform, technology, and services',
          'The Services are protected by copyright, trademark, and other laws',
          'You may not copy, modify, or create derivative works of our Services',
        ],
      },
      { type: 'h3', text: '5.3 Processed Content' },
      {
        type: 'ul',
        items: [
          'Output videos remain your property',
          'We retain the right to use anonymized data for service improvement',
          'Voice clones created through our service may not be used to impersonate individuals without consent',
        ],
      },
      { type: 'h3', text: '5.4 Demonstration Content' },
      {
        type: 'ul',
        items: [
          'Demonstration videos on our website are for illustrative purposes only',
          'Such demos do not imply endorsement by or affiliation with the original content creators',
          'All demonstration content remains property of respective copyright holders',
        ],
      },
    ],
  },
  {
    id: 'payment',
    number: 6,
    title: 'Payment Terms',
    blocks: [
      { type: 'h3', text: '6.1 Fees' },
      {
        type: 'ul',
        items: [
          'Fees are based on usage as specified in your service agreement',
          'All fees are non-refundable except as required by law',
          "We may modify fees with 30 days' notice",
        ],
      },
      { type: 'h3', text: '6.2 Billing' },
      {
        type: 'ul',
        items: [
          'Organizations are billed monthly in arrears',
          'Payment is due within 30 days of invoice',
          'Late payments may result in service suspension',
        ],
      },
      { type: 'h3', text: '6.3 Taxes' },
      { type: 'p', text: 'You are responsible for all applicable taxes, excluding taxes based on our net income.' },
    ],
  },
  {
    id: 'sla',
    number: 7,
    title: 'Service Level and Support',
    blocks: [
      { type: 'h3', text: '7.1 Availability' },
      {
        type: 'ul',
        items: [
          'We strive for 99.9% uptime but do not guarantee uninterrupted service',
          'Scheduled maintenance will be communicated in advance',
          'Support is provided during business hours via email',
        ],
      },
      { type: 'h3', text: '7.2 Processing Times' },
      { type: 'p', text: 'Processing times vary based on video length and system load. We do not guarantee specific turnaround times.' },
    ],
  },
  {
    id: 'ip-indemnification',
    number: 8,
    title: 'Intellectual Property Indemnification',
    blocks: [
      { type: 'h3', text: '8.1 Your Indemnification' },
      { type: 'p', text: 'You will defend, indemnify, and hold us harmless from claims arising from:' },
      {
        type: 'ul',
        items: [
          'Your content or use of the Services',
          'Violation of these Terms',
          'Infringement of intellectual property rights',
          'Unauthorized use of voice cloning technology',
        ],
      },
      { type: 'h3', text: '8.2 Our Indemnification' },
      { type: 'p', text: 'We will defend you against claims that our Services infringe third-party intellectual property rights, subject to limitations.' },
    ],
  },
  {
    id: 'disclaimers',
    number: 9,
    title: 'Disclaimers',
    blocks: [
      {
        type: 'warning',
        bold: 'Important Disclaimer',
        text: 'THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.',
      },
      { type: 'p', text: 'WE SPECIFICALLY DISCLAIM:' },
      {
        type: 'ul',
        items: [
          'WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE',
          'WARRANTIES REGARDING ACCURACY OF TRANSLATIONS',
          'WARRANTIES ABOUT THIRD-PARTY SERVICES OR INTEGRATIONS',
          'LIABILITY FOR CONTENT CREATED USING OUR SERVICES',
        ],
      },
    ],
  },
  {
    id: 'liability',
    number: 10,
    title: 'Limitation of Liability',
    blocks: [
      { type: 'h3', text: '10.1 Liability Cap' },
      {
        type: 'warning',
        bold: 'Cap on Damages',
        text: 'OUR TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU IN THE 12 MONTHS PRECEDING THE CLAIM.',
      },
      { type: 'h3', text: '10.2 Exclusions' },
      { type: 'p', text: 'WE ARE NOT LIABLE FOR:' },
      {
        type: 'ul',
        items: [
          'INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES',
          'LOST PROFITS OR BUSINESS OPPORTUNITIES',
          'LOSS OR CORRUPTION OF DATA',
          'THIRD-PARTY SERVICE FAILURES',
        ],
      },
    ],
  },
  {
    id: 'confidentiality',
    number: 11,
    title: 'Confidentiality',
    blocks: [
      { type: 'h3', text: '11.1 Confidential Information' },
      { type: 'p', text: "Each party will protect the other's confidential information with reasonable care." },
      { type: 'h3', text: '11.2 Exceptions' },
      { type: 'p', text: 'Confidentiality obligations do not apply to information that:' },
      {
        type: 'ul',
        items: [
          'Is publicly available',
          'Was known prior to disclosure',
          'Is independently developed',
          'Must be disclosed by law',
        ],
      },
    ],
  },
  {
    id: 'termination',
    number: 12,
    title: 'Term and Termination',
    blocks: [
      { type: 'h3', text: '12.1 Term' },
      { type: 'p', text: 'These Terms continue until terminated by either party.' },
      { type: 'h3', text: '12.2 Termination' },
      {
        type: 'ul',
        items: [
          "Either party may terminate with 30 days' notice",
          'We may terminate immediately for Terms violations',
          'Upon termination, your access will be disabled',
          'Content will be deleted per our data retention policy',
        ],
      },
      { type: 'h3', text: '12.3 Survival' },
      { type: 'p', text: 'Sections relating to ownership, indemnification, disclaimers, and limitations survive termination.' },
    ],
  },
  {
    id: 'white-label',
    number: 13,
    title: 'White-Label Terms',
    blocks: [
      { type: 'h3', text: '13.1 White-Label Services' },
      { type: 'p', text: 'Organizations may request white-label instances subject to separate agreement.' },
      { type: 'h3', text: '13.2 Branding' },
      { type: 'p', text: 'White-label customers may customize branding but must include "Powered by Dub Labs" attribution.' },
    ],
  },
  {
    id: 'modifications',
    number: 14,
    title: 'Modifications to Terms',
    blocks: [
      { type: 'p', text: 'We may modify these Terms at any time. Material changes will be notified via email or through the Services. Continued use constitutes acceptance of modified Terms.' },
    ],
  },
  {
    id: 'governing-law',
    number: 15,
    title: 'Governing Law and Disputes',
    blocks: [
      { type: 'h3', text: '15.1 Governing Law' },
      { type: 'p', text: 'These Terms are governed by the laws of New Jersey, USA, without regard to conflict of law principles.' },
      { type: 'h3', text: '15.2 Dispute Resolution' },
      {
        type: 'ul',
        items: [
          'Disputes will first be addressed through good faith negotiations',
          'If unresolved, disputes will be subject to binding arbitration in New Jersey',
          'You waive the right to participate in class actions',
        ],
      },
    ],
  },
  {
    id: 'general',
    number: 16,
    title: 'General Provisions',
    blocks: [
      { type: 'h3', text: '16.1 Entire Agreement' },
      { type: 'p', text: 'These Terms constitute the entire agreement between the parties.' },
      { type: 'h3', text: '16.2 Severability' },
      { type: 'p', text: 'If any provision is unenforceable, the remaining provisions continue in effect.' },
      { type: 'h3', text: '16.3 Assignment' },
      { type: 'p', text: 'You may not assign these Terms without our consent. We may assign our rights to a successor entity.' },
      { type: 'h3', text: '16.4 Force Majeure' },
      { type: 'p', text: 'Neither party is liable for failures due to circumstances beyond reasonable control.' },
    ],
  },
];

const CONTACT = {
  name: 'Dub Labs',
  email: 'hello@aiguru.in',
  address: 'Gurukul Road, Ahmedabad, Gujarat – 380052',
};

export default function TermsPage() {
  useSEO({
    title: 'Terms of Service — DubLabs AI Video Dubbing Platform',
    description:
      'Terms and conditions for using the DubLabs AI video dubbing services. Interactive terms document with searchable sections and section-level navigation.',
    canonical: 'https://dublabs.ai/terms',
    breadcrumbs: [
      { name: 'Legal', url: 'https://dublabs.ai/terms' },
      { name: 'Terms of Service', url: 'https://dublabs.ai/terms' },
    ],
    dateModified: '2026-07-30',
  });

  return (
    <LegalDocument
      eyebrow="Legal"
      title="Terms of Service"
      subtitle="The rules of the road for using DubLabs. Read what matters most — search, jump, and skim as you like."
      lastUpdated="June 2025"
      icon={<AnimatedGavel size={120} />}
      sections={SECTIONS}
      contact={CONTACT}
    />
  );
}
